import type { ObservationCategoryCode } from '@seen/core/observation-logic/derivations/rules'
import {
  fetchLoincPanel,
  instrumentFromLoincPanel,
  type LoincPanelDefinition,
  loincPanelGapMessage,
} from '@seen/core/terminology/instruments/loincPanels'
import type { QueryClient } from '@tanstack/react-query'

// What placing a LOINC panel with no instrument in source comes to, from its
// NLM definition.
export type LoincPanelPlacement =
  | {
      kind: 'panel'
      attrs: {
        templateId: string
        version: string
        definition: LoincPanelDefinition
      }
    }
  // NLM has the panel, but not enough of it to record: the person can ask
  // for it to be added to Orbit.
  | { kind: 'unavailable'; message: string; request: string }
  // NLM has no form for the code.
  | { kind: 'unknown' }

// NLM definitions change only with a LOINC release.
const STALE_TIME = 24 * 60 * 60 * 1000

export async function placeLoincPanel(
  queryClient: QueryClient,
  code: string,
  display: string,
  // From the panel's LOINC class, as the terminology server gives it.
  category: ObservationCategoryCode | undefined
): Promise<LoincPanelPlacement> {
  const fetched = await loincPanelDefinition(queryClient, code)
  if (!fetched) {
    return { kind: 'unknown' }
  }
  const definition = category ? { ...fetched, category } : fetched
  const { template, gaps } = instrumentFromLoincPanel(definition)
  if (template) {
    return {
      kind: 'panel',
      attrs: { templateId: template.id, version: template.version, definition },
    }
  }
  const name = definition.name || display || code
  const reasons = gaps.map(loincPanelGapMessage)
  return {
    kind: 'unavailable',
    message:
      `${name} can't be recorded in Orbit yet. ${reasons[0] ?? ''}`.trim(),
    request: [
      `Please add the LOINC panel "${name}" (${code}) to Orbit's structured capture.`,
      ...reasons,
      '',
      'Where we use it: ',
    ].join('\n'),
  }
}

function loincPanelDefinition(
  queryClient: QueryClient,
  code: string
): Promise<LoincPanelDefinition | undefined> {
  return queryClient.fetchQuery({
    queryKey: ['nlm-loinc-panel', code],
    queryFn: ({ signal }) => fetchLoincPanel(code, { signal }),
    staleTime: STALE_TIME,
  })
}

// Whether NLM has a form for the panel, so placing it can do something: one
// it lacks is never placed. Fetched as placing it would, so a panel offered
// is then placed without waiting. NLM being out keeps it, to be told then.
export async function loincPanelHasForm(
  queryClient: QueryClient,
  code: string
): Promise<boolean> {
  return loincPanelDefinition(queryClient, code).then(
    (definition) => !!definition,
    () => true
  )
}
